import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import {
  decideConsultantProposal,
  matchConsultantProposalAgainstNeeds,
} from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import ConsultantProposalMatchesModal from "./ConsultantProposalMatchesModal";
import { StatusBadge } from "../../../components/Portal/ui";
import { PROPOSAL_STATUS_COLORS } from "../../../constants/statuses";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

/**
 * Skill chip group — mirrors Pilot's ConsultantDetailModal styling exactly:
 * Chakra <Badge variant="subtle" fontSize="sm" px={2} py={1}> with the
 * type-specific colorScheme:
 *   - technical → purple
 *   - functional → orange
 *   - business → green
 */
const SkillBadges = ({ items, colorScheme }) => {
  if (!items || items.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500">
        —
      </Text>
    );
  }
  return (
    <HStack wrap="wrap" spacing={1}>
      {items.map((s, i) => (
        <Badge
          key={`${s}-${i}`}
          colorScheme={colorScheme}
          variant="subtle"
          fontSize="sm"
          px={2}
          py={1}
        >
          {s}
        </Badge>
      ))}
    </HStack>
  );
};

const SkillSectionLabel = ({ children }) => (
  <Text
    fontSize="xs"
    textTransform="uppercase"
    letterSpacing="wider"
    color="gray.500"
    mb={1}
    sx={{ marginBottom: "4px !important" }}
  >
    {children}
  </Text>
);

const ConsultantProposalDetailModal = ({
  isOpen,
  onClose,
  proposal,
  isReceiver,
  onChanged,
}) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, features } = useBusinessUnit();
  const hasPilot = Boolean(features?.has_pilot);

  const [decisionNote, setDecisionNote] = useState("");
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [matches, setMatches] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const matchesModal = useDisclosure();

  useEffect(() => {
    setDecisionNote("");
    setMatches(null);
  }, [isOpen, proposal?.id]);

  if (!proposal) return null;

  const decide = async (decision) => {
    setDecisionLoading(true);
    try {
      await decideConsultantProposal(
        proposal.id,
        { decision, note: decisionNote || null },
        { buId: selectedBusinessUnitId || undefined }
      );
      toast({
        title:
          decision === "accept"
            ? t("partners.consultantProposals.toasts.accepted", "Proposal accepted")
            : t("partners.consultantProposals.toasts.declined", "Proposal declined"),
        status: "success",
      });
      onChanged?.();
      onClose();
    } catch (err) {
      toast({
        title: t(
          "partners.consultantProposals.errors.decisionFailed",
          "Failed to record decision"
        ),
        description: err?.response?.data?.detail || err?.message,
        status: "error",
      });
    } finally {
      setDecisionLoading(false);
    }
  };

  const runMatch = async () => {
    setMatchLoading(true);
    try {
      const res = await matchConsultantProposalAgainstNeeds(proposal.id, {
        buId: selectedBusinessUnitId || undefined,
      });
      setMatches(res?.matches || []);
    } catch (err) {
      toast({
        title: t(
          "partners.consultantProposals.errors.matchFailed",
          "Failed to score matches"
        ),
        description: err?.response?.data?.detail || err?.message,
        status: "error",
      });
    } finally {
      setMatchLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          <Flex align="center" gap={3}>
            <Heading size="md" sx={{ marginBottom: "0 !important" }}>
              {t(
                "partners.consultantProposals.detail.title",
                "Consultant Proposal"
              )}
            </Heading>
            <StatusBadge
              label={t(`partners.consultantProposals.statusLabels.${proposal.status}`, proposal.status)}
              colorScheme={PROPOSAL_STATUS_COLORS[proposal.status] || "gray"}
            />
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="info" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm">
                {t(
                  "partners.consultantProposals.detail.anonymisedNotice",
                  "This is an anonymised view — name, email, and phone are not shared."
                )}
              </Text>
            </Alert>

            <Box>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                {t("partners.consultantProposals.detail.sender", "From")}
              </Text>
              <Text>
                {proposal.senderCompanyName ||
                  `${t("partners.consultantProposals.partnerNumber", "Partner")} #${proposal.senderCompanyId}`}
              </Text>
            </Box>

            {proposal.title && (
              <Box>
                <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                  {t("partners.consultantProposals.detail.role", "Role")}
                </Text>
                <Text fontWeight="medium">{proposal.title}</Text>
              </Box>
            )}

            {proposal.description && (
              <Box>
                <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                  {t(
                    "partners.consultantProposals.detail.description",
                    "Profile"
                  )}
                </Text>
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {proposal.description}
                </Text>
              </Box>
            )}

            <HStack spacing={6} wrap="wrap">
              <Box>
                <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                  {t(
                    "partners.consultantProposals.detail.dailyRate",
                    "Daily rate"
                  )}
                </Text>
                <Text>
                  {proposal.dailyRate
                    ? `${proposal.dailyRate} ${proposal.currency || "EUR"}`
                    : "—"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                  {t(
                    "partners.consultantProposals.detail.availability",
                    "Available from"
                  )}
                </Text>
                <Text>
                  {proposal.availabilityDate
                    ? new Date(proposal.availabilityDate).toLocaleDateString()
                    : "—"}
                </Text>
              </Box>
              {proposal.location && (
                <Box>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                    {t(
                      "partners.consultantProposals.detail.location",
                      "Location"
                    )}
                  </Text>
                  <Text>{proposal.location}</Text>
                </Box>
              )}
            </HStack>

            <Divider />

            <Box>
              <SkillSectionLabel>
                {t(
                  "partners.consultantProposals.detail.skillsTechnical",
                  "Technical skills"
                )}
              </SkillSectionLabel>
              <SkillBadges
                items={proposal.skillsTechnical}
                colorScheme="purple"
              />
            </Box>
            <Box>
              <SkillSectionLabel>
                {t(
                  "partners.consultantProposals.detail.skillsFunctional",
                  "Functional skills"
                )}
              </SkillSectionLabel>
              <SkillBadges
                items={proposal.skillsFunctional}
                colorScheme="orange"
              />
            </Box>
            <Box>
              <SkillSectionLabel>
                {t(
                  "partners.consultantProposals.detail.skillsBusiness",
                  "Business skills"
                )}
              </SkillSectionLabel>
              <SkillBadges
                items={proposal.skillsBusiness}
                colorScheme="green"
              />
            </Box>

            {isReceiver && proposal.status === "pending" && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase" mb={1}>
                    {t(
                      "partners.consultantProposals.detail.note",
                      "Note (optional)"
                    )}
                  </Text>
                  <Textarea
                    value={decisionNote}
                    onChange={(e) => setDecisionNote(e.target.value)}
                    placeholder={t(
                      "partners.consultantProposals.detail.notePlaceholder",
                      "Anything to share with the sender..."
                    )}
                    rows={2}
                  />
                </Box>
              </>
            )}

            {isReceiver && hasPilot && (
              <>
                <Divider />
                <Box>
                  <Flex align="center" justify="space-between" mb={2}>
                    <Heading size="sm" sx={{ marginBottom: "0 !important" }}>
                      {t(
                        "partners.consultantProposals.detail.matchTitle",
                        "Match against my open needs"
                      )}
                    </Heading>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={runMatch}
                      isLoading={matchLoading}
                    >
                      {t(
                        "partners.consultantProposals.detail.runMatch",
                        "Run scoring"
                      )}
                    </Button>
                  </Flex>
                  {matches !== null && (
                    matches.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">
                        {t(
                          "partners.consultantProposals.detail.noMatches",
                          "No matching needs above the threshold."
                        )}
                      </Text>
                    ) : (
                      <Flex
                        align="center"
                        justify="space-between"
                        gap={3}
                        wrap="wrap"
                        bg="gray.50"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        px={3}
                        py={2}
                      >
                        <Text fontSize="sm" sx={{ marginBottom: "0 !important" }}>
                          {t(
                            "partners.consultantProposals.detail.matchesFound",
                            {
                              count: matches.length,
                              defaultValue: "{{count}} matching open need(s) found",
                            }
                          )}
                        </Text>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={matchesModal.onOpen}
                        >
                          {t(
                            "partners.consultantProposals.detail.viewMatches",
                            "View matches"
                          )}
                        </Button>
                      </Flex>
                    )
                  )}
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          {isReceiver && proposal.status === "pending" ? (
            <HStack spacing={3}>
              <Button
                colorScheme="red"
                variant="outline"
                size="sm"
                onClick={() => decide("decline")}
                isLoading={decisionLoading}
              >
                {t(
                  "partners.consultantProposals.detail.decline",
                  "Decline"
                )}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => decide("accept")}
                isLoading={decisionLoading}
              >
                {t("partners.consultantProposals.detail.accept", "Accept")}
              </Button>
            </HStack>
          ) : (
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t("common.close", "Close")}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>

      <ConsultantProposalMatchesModal
        isOpen={matchesModal.isOpen}
        onClose={matchesModal.onClose}
        matches={matches || []}
      />
    </Modal>
  );
};

export default ConsultantProposalDetailModal;
